// habitSchedule.js — single source of truth for "is this habit scheduled
// on this date?".
//
// The habit object carries a `frequency` ('Daily' | 'Weekdays' | 'Custom')
// and, for Custom, a `customDays` array of weekday indexes (0 = Sunday).
// On top of that, the user may have forward-only-edited the schedule; in
// that case a record in habitScheduleOverrides says "use THIS schedule
// for days BEFORE frequencyChangedAt, and the habit's current schedule
// for days ON or AFTER." See habitScheduleOverrides.js.
//
// All scheduling questions in the app should go through here so an override
// applied in one place (e.g. Dashboard grid) is honored everywhere else
// (Analytics, completion rings, etc.) without having to remember to branch.

import { getOverride } from './habitScheduleOverrides.js';

// Build the ISO date string for a (year, month, day) triple. `month` is
// 0-indexed in JS Date land, so we +1 when printing.
function isoFor(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

// day-of-week for a (y, m, d) in LOCAL time — `new Date(y,m,d)` is local,
// matching how the Dashboard grid renders dates.
function dowOf(y, m, d) {
  return new Date(y, m, d).getDay();
}

// Returns the pair { frequency, customDays } that applies to the given
// date — consulting the forward-only override first. `customDays` is null
// for Daily/Weekdays, and an array for Custom.
export function effectiveSchedule(habit, y, m, d) {
  const ov = getOverride(habit?.id);
  if (ov && ov.frequencyChangedAt) {
    const thisIso = isoFor(y, m, d);
    // Anything STRICTLY before the cutoff uses the previous schedule.
    if (thisIso < ov.frequencyChangedAt) {
      return {
        frequency: ov.prevFrequency || 'Daily',
        customDays: Array.isArray(ov.prevCustomDays) ? ov.prevCustomDays : null,
      };
    }
  }
  return {
    frequency: habit?.frequency || 'Daily',
    customDays: Array.isArray(habit?.customDays) ? habit.customDays : null,
  };
}

// True if the habit is scheduled to occur on (y, m, d). `Daily` is always
// true; `Weekdays` is Mon–Fri; `Custom` consults the stored day-of-week set.
export function isHabitScheduledOn(habit, y, m, d) {
  const { frequency, customDays } = effectiveSchedule(habit, y, m, d);
  if (frequency === 'Daily') return true;
  const dow = dowOf(y, m, d);
  if (frequency === 'Weekdays') return dow >= 1 && dow <= 5;
  if (frequency === 'Custom')  return Array.isArray(customDays) ? customDays.includes(dow) : true;
  return true;
}

// Convenience wrapper for callers that already have an ISO string.
export function isHabitScheduledOnIso(habit, iso) {
  if (!iso || iso.length < 10) return true;
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7)) - 1;
  const d = Number(iso.slice(8, 10));
  return isHabitScheduledOn(habit, y, m, d);
}
